import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Array<Record<string, any>> | undefined} */
  #variants;

  connectedCallback() {
    super.connectedCallback();
    this.#initRadios();
    this.addEventListener('change', this.variantChanged.bind(this));
    this.#syncInitialVariantState();
  }

  /**
   * Builds (or rebuilds) the cached radio/index arrays from the current DOM.
   * Must be called after every morph so animation state stays in sync.
   */
  #initRadios() {
    this.#checkedIndices = [];
    this.#radios = [];
    this.#variants = undefined;
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      let checkedIndex = -1;
      radios.forEach((radio, index) => {
        if (radio.checked) {
          checkedIndex = index;
          // Sync data-current-checked to the actual checked state.
          // morph preserves data-current-checked from the old DOM (stale), so we must
          // re-align it here so CSS animations use the correct post-morph selection.
          radio.dataset.currentChecked = 'true';
        } else {
          radio.dataset.currentChecked = 'false';
        }
      });

      if (checkedIndex !== -1) {
        this.#checkedIndices.push([checkedIndex]);
      }
    });
  }

  #getVariants() {
    if (this.#variants) return this.#variants;

    const script = this.querySelector('script[data-variant-picker-variants]');
    if (!script?.textContent) return [];

    try {
      const parsed = JSON.parse(script.textContent);
      this.#variants = Array.isArray(parsed) ? parsed : [];
      return this.#variants;
    } catch (error) {
      console.error('Failed to parse variant picker variants:', error);
      return [];
    }
  }

  #getSelectedVariant() {
    const script = this.querySelector('script[data-selected-variant]');
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Failed to parse selected variant:', error);
      return null;
    }
  }

  #normalizeOptionValue(value) {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[–—−]/g, '-')
      .replace(/\s+/g, ' ');
  }

  #extractNumericValue(value) {
    const match = String(value ?? '').replace(',', '.').match(/(\d+(?:\.\d+)?)/);
    return match ? Number(match[1]) : null;
  }

  #getOptionControls() {
    return Array.from(this.querySelectorAll('fieldset[data-option-position], select[data-option-position]')).sort(
      (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
    );
  }

  #getSelectedOptionTexts() {
    return this.#getOptionControls().map((control) => {
      if (control instanceof HTMLSelectElement) return control.value || null;

      const checked = control.querySelector('input:checked');
      return checked instanceof HTMLInputElement ? checked.value || null : null;
    });
  }

  #findControlInputByValue(control, optionText) {
    const normalizedTarget = this.#normalizeOptionValue(optionText);

    if (control instanceof HTMLSelectElement) {
      return (
        Array.from(control.options).find(
          (candidate) => this.#normalizeOptionValue(candidate.value) === normalizedTarget
        ) ?? null
      );
    }

    return (
      Array.from(control.querySelectorAll('input')).find(
        (candidate) => this.#normalizeOptionValue(candidate.value) === normalizedTarget
      ) ?? null
    );
  }

  #getOptionValueIdForControl(control, optionText) {
    const normalizedTarget = this.#normalizeOptionValue(optionText);

    if (control instanceof HTMLSelectElement) {
      const option = Array.from(control.options).find(
        (candidate) => this.#normalizeOptionValue(candidate.value) === normalizedTarget
      );
      return option?.dataset.optionValueId ?? null;
    }

    const input = Array.from(control.querySelectorAll('input')).find(
      (candidate) => this.#normalizeOptionValue(candidate.value) === normalizedTarget
    );
    return input?.dataset.optionValueId ?? null;
  }

  #findBestVariantForSelection(changedFieldsetIndex) {
    const variants = this.#getVariants();
    const selectedTexts = this.#getSelectedOptionTexts();

    if (!variants.length || !selectedTexts.length) return null;

    const normalizedSelections = selectedTexts.map((value) => this.#normalizeOptionValue(value));
    const prefixSelections = normalizedSelections.slice(0, changedFieldsetIndex + 1);

    const candidates = variants.filter((variant) => {
      const variantOptions = [variant.option1, variant.option2, variant.option3].map((value) =>
        this.#normalizeOptionValue(value)
      );

      return prefixSelections.every((selection, index) => !selection || variantOptions[index] === selection);
    });

    if (!candidates.length) return null;

    const scoredCandidates = candidates.map((variant, originalIndex) => {
      const variantOptions = [variant.option1, variant.option2, variant.option3];
      let exactMatches = 0;
      let numericDistance = 0;

      for (let index = changedFieldsetIndex + 1; index < selectedTexts.length; index++) {
        const currentSelection = selectedTexts[index];
        const variantOption = variantOptions[index];

        if (!currentSelection || !variantOption) continue;

        if (this.#normalizeOptionValue(currentSelection) === this.#normalizeOptionValue(variantOption)) {
          exactMatches += 1;
          continue;
        }

        const currentNumber = this.#extractNumericValue(currentSelection);
        const variantNumber = this.#extractNumericValue(variantOption);
        if (currentNumber !== null && variantNumber !== null) {
          numericDistance += Math.abs(currentNumber - variantNumber);
        } else {
          numericDistance += 1000;
        }
      }

      return {
        variant,
        availableScore: variant.available ? 1 : 0,
        exactMatches,
        numericDistance,
        originalIndex,
      };
    });

    scoredCandidates.sort((a, b) => {
      if (b.availableScore !== a.availableScore) return b.availableScore - a.availableScore;
      if (b.exactMatches !== a.exactMatches) return b.exactMatches - a.exactMatches;
      if (a.numericDistance !== b.numericDistance) return a.numericDistance - b.numericDistance;
      return a.originalIndex - b.originalIndex;
    });

    return scoredCandidates[0]?.variant ?? null;
  }

  #getPreferredOptionValueIds(selectedOption) {
    const fieldsetIndex = Number.parseInt(selectedOption.dataset.fieldsetIndex ?? '');
    if (Number.isNaN(fieldsetIndex)) return this.selectedOptionsValues;

    const bestVariant = this.#findBestVariantForSelection(fieldsetIndex);
    if (!bestVariant) return this.#getSelectedOptionsValuesUpTo(fieldsetIndex);

    const controls = this.#getOptionControls();
    const optionTexts = [bestVariant.option1, bestVariant.option2, bestVariant.option3];
    const optionValueIds = [];

    controls.forEach((control, index) => {
      const optionText = optionTexts[index];
      if (!optionText) return;

      const optionValueId = this.#getOptionValueIdForControl(control, optionText);
      if (optionValueId) optionValueIds.push(optionValueId);
    });

    return optionValueIds.length ? optionValueIds : this.#getSelectedOptionsValuesUpTo(fieldsetIndex);
  }

  #clearCurrentSelectionUnavailableState() {
    this.querySelectorAll('[data-current-selection-unavailable]').forEach((element) => {
      element.removeAttribute('data-current-selection-unavailable');
    });
  }

  #syncCurrentSelectionUnavailableState(variantData) {
    this.#clearCurrentSelectionUnavailableState();

    if (variantData?.available !== false) return;

    this.querySelectorAll('fieldset input:checked, select option[selected]').forEach((element) => {
      element.setAttribute('data-current-selection-unavailable', 'true');
    });
  }

  #findBestInitialVariant() {
    const selectedVariant = this.#getSelectedVariant();
    if (selectedVariant?.available) return selectedVariant;

    const variants = this.#getVariants();
    if (!variants.length) return null;

    return variants.find((variant) => variant.available) ?? variants[0] ?? null;
  }

  #syncInitialVariantState() {
    requestAnimationFrame(() => {
      const bestVariant = this.#findBestInitialVariant();
      if (!bestVariant?.options?.length) return;

      const controls = this.#getOptionControls();
      if (!controls.length) return;

      let finalTarget = null;
      let needsSync = false;

      controls.forEach((control, index) => {
        const optionText = bestVariant.options[index];
        if (!optionText) return;

        const targetOption = this.#findControlInputByValue(control, optionText);
        if (!targetOption) return;

        if (targetOption instanceof HTMLOptionElement) {
          if (targetOption.selected && control.value === targetOption.value) return;

          control.value = targetOption.value;
          Array.from(control.options).forEach((option) => {
            option.toggleAttribute('selected', option === targetOption);
          });
          finalTarget = control;
          needsSync = true;
          return;
        }

        if (targetOption.checked) return;

        targetOption.checked = true;
        finalTarget = targetOption;
        needsSync = true;
      });

      if (!needsSync || !(finalTarget instanceof HTMLElement)) return;

      this.updateSelectedOption(finalTarget);
      this.dispatchEvent(new VariantSelectedEvent({ id: finalTarget.dataset.optionValueId ?? '' }));
      this.fetchUpdatedSection(this.buildRequestUrl(finalTarget));
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Gets selected option values only up to and including the given fieldset index.
   * Prevents stale downstream selections from blocking valid variant combinations.
   * @param {number} fieldsetIndex
   * @returns {string[]}
   */
  #getSelectedOptionsValuesUpTo(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const result = [];
    for (let i = 0; i <= fieldsetIndex; i++) {
      const fieldset = fieldsets[i];
      if (!fieldset) break;
      const checked = /** @type {HTMLElement | null} */ (fieldset.querySelector('input:checked'));
      if (checked?.dataset.optionValueId) {
        result.push(checked.dataset.optionValueId);
      }
    }
    return result;
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (!source) {
      const optionValues = this.#getPreferredOptionValueIds(selectedOption);
      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[data-selected-variant]`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            const variantData = JSON.parse(textContent);
            this.#syncCurrentSelectionUnavailableState(variantData);

            // Update the URL with the resolved variant id (covers cascade auto-selections)
            const isOnProductPage =
              this.dataset.templateProductMatch === 'true' &&
              !this.closest('product-card') &&
              !this.closest('quick-add-dialog');
            if (isOnProductPage && variantData?.id) {
              const url = new URL(window.location.href);
              url.searchParams.set('variant', variantData.id);
              if (url.href !== window.location.href) {
                requestYieldCallback(() => history.replaceState({}, '', url.toString()));
              }
            }

            this.dispatchEvent(
              new VariantUpdateEvent(variantData, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    this.#initRadios();

    return newProduct;
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
